import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useAPI } from '../../../Hooks/useAPIs';
import UserAutocomplete from '../../../Hooks/useUserAutocomplete';
import { sanitizeMacAddress } from '../../../utils/macAddress';
import { DEVICE_TYPE_OPTIONS, formatDeviceType } from '../../../utils/deviceTypes';

const AddMultipleDevicesDialog = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  enableUserAssignment = false,
  enableRegionSelection = false,
  REGIONS = [],
}) => {
  const [devices, setDevices] = useState([]);
  const [currentDevice, setCurrentDevice] = useState({ 
    pi: '', 
    deviceType: '', 
    region_code: '',
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [bulkMacInput, setBulkMacInput] = useState('');
  const [isCheckingDevices, setIsCheckingDevices] = useState(false);
  const [deviceCheckResults, setDeviceCheckResults] = useState(null);

  const { apiCall } = useAPI();

  const handleRemoveDevice = (index) => {
    setDevices(devices.filter((_, i) => i !== index));
  };

  const handleBulkAddMacAddresses = async () => {
    console.log('handleBulkAddMacAddresses called', { enableUserAssignment, enableRegionSelection });
    setError('');
    setDeviceCheckResults(null);
    
    if (!bulkMacInput.trim()) {
      setError('Please paste MAC addresses');
      return;
    }

    if (enableUserAssignment) {
      if (!currentDevice.pi) {
        setError('Please select a PI before adding MAC addresses');
        return;
      }

      if (currentDevice.pi === 'other' && !currentDevice.deviceType) {
        setError('Please select the device type before adding MAC addresses');
        return;
      }
    }

    if (enableRegionSelection && enableUserAssignment && !selectedUser && !currentDevice.region_code) {
      setError('Please select a region before adding MAC addresses (required when user is not selected)');
      return;
    }
    
    if (enableRegionSelection && !enableUserAssignment && !currentDevice.region_code) {
      setError('Please select a region before adding MAC addresses');
      return;
    }

    const macList = bulkMacInput
      .split(',')
      .map(mac => sanitizeMacAddress(mac.trim()))
      .filter(mac => mac.length > 0);

    if (macList.length === 0) {
      setError('No valid MAC addresses found. Please separate MAC addresses with commas.');
      return;
    }

    const invalidMacs = macList.filter(mac => !/^[A-Za-z0-9]{12,}$/.test(mac));
    if (invalidMacs.length > 0) {
      setError(`Invalid MAC addresses found: ${invalidMacs.slice(0, 5).join(', ')}${invalidMacs.length > 5 ? '...' : ''}. Each MAC address must be at least 12 alphanumeric characters.`);
      return;
    }

    const uniqueMacs = [...new Set(macList.map(mac => mac.toLowerCase()))];
    if (uniqueMacs.length !== macList.length) {
      setError('Duplicate MAC addresses found in the pasted list. Please remove duplicates.');
      return;
    }

    const existingMacs = new Set(devices.map(d => d.macAddress.toLowerCase()));
    const duplicateMacs = macList.filter(mac => existingMacs.has(mac.toLowerCase()));
    if (duplicateMacs.length > 0) {
      setError(`Some MAC addresses are already in the list: ${duplicateMacs.slice(0, 5).join(', ')}${duplicateMacs.length > 5 ? '...' : ''}`);
      return;
    }

    if (!enableUserAssignment) {
      console.log('[AddMultipleDevicesDialog] User view detected - checking device ownership');
      console.log('[AddMultipleDevicesDialog] MAC addresses to check:', macList);
      setIsCheckingDevices(true);
      
      try {
        console.log('[AddMultipleDevicesDialog] Making API call to api/v1/devices/check-ownership');
        const response = await apiCall('POST', 'api/v1/devices/check-ownership', {
          mac_addresses: macList
        });
        console.log('[AddMultipleDevicesDialog] API call successful. Response:', response);
        console.log('[AddMultipleDevicesDialog] Response data:', response.data);
        
        const results = response.data;
        setDeviceCheckResults(results);
        
        const devicesWithOwners = results?.devices_with_owners || [];
        if (devicesWithOwners.length > 0) {
          const ownerMacs = devicesWithOwners.slice(0, 5).map(d => d.mac_addr || d.mac_address || d).join(', ');
          const errorMsg = `Some devices already have owners: ${ownerMacs}${devicesWithOwners.length > 5 ? '...' : ''}. Please remove them or contact support.`;
          console.log('[AddMultipleDevicesDialog] Devices with owners found:', devicesWithOwners);
          setError(errorMsg);
          setIsCheckingDevices(false);
          return;
        }
        console.log('[AddMultipleDevicesDialog] No devices with owners found. Proceeding...');
      } catch (error) {
        console.error('[AddMultipleDevicesDialog] Device ownership check failed:', error);
        console.error('[AddMultipleDevicesDialog] Error response:', error.response);
        console.error('[AddMultipleDevicesDialog] Error details:', error.response?.data || error.message);
        const errorMsg = `Warning: Could not verify device ownership. ${error.response?.data?.detail || error.message || 'API endpoint may not exist.'} Proceeding with device addition.`;
        setError(errorMsg);
        setDeviceCheckResults(null);
      } finally {
        setIsCheckingDevices(false);
        console.log('[AddMultipleDevicesDialog] Device check completed');
      }
    } else {
      console.log('[AddMultipleDevicesDialog] Admin view detected - skipping ownership check (enableUserAssignment=true)');
    }

    const newDevices = macList.map(macAddress => ({
      macAddress,
      macInputValue: macAddress,
      pi: currentDevice.pi,
      deviceType: currentDevice.deviceType,
      region_code: currentDevice.region_code,
      macError: ''
    }));

    setDevices([...devices, ...newDevices]);
    setBulkMacInput('');
    setDeviceCheckResults(null);
  };

  const handleCurrentDeviceChange = (field, value) => {
    const updated = { ...currentDevice, [field]: value };
    
    if (field === 'pi' && value !== 'other') {
      updated.deviceType = '';
    }
    
    setCurrentDevice(updated);
  };

  const handleSubmit = () => {
    setError('');

    if (devices.length === 0) {
      setError('Please add at least one device');
      return;
    }

    const validDevices = devices.map(device => ({
      mac_addr: sanitizeMacAddress(device.macAddress),
      pi: device.pi || undefined,
      device_type: device.pi === 'other' ? device.deviceType : undefined,
      region: enableRegionSelection && device.region_code 
        ? REGIONS.find(r => r.code === device.region_code)?.id 
        : undefined,
    }));

    const requestData = {
      devices: validDevices,
      notes: notes.trim() || undefined,
      user: enableUserAssignment && selectedUser ? selectedUser.id : undefined,
    };

    onSubmit(requestData);
    
    setDevices([]);
    setCurrentDevice({ 
      pi: '', 
      deviceType: '', 
      region_code: '',
    });
    setSelectedUser(null);
    setNotes('');
    setError('');
    setBulkMacInput('');
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const getDeviceLabel = (device) => {
    const lastFour = device.macAddress.slice(-4).toUpperCase();
    const type = device.pi === 'other' ? formatDeviceType(device.deviceType) : device.pi.toUpperCase();
    return `...${lastFour} - ${type}`;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Multiple Devices</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
        {deviceCheckResults && deviceCheckResults.devices_with_owners?.length === 0 && (
          <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
            All devices are available (no existing owners found).
          </Alert>
        )}

        {devices.length > 0 && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              mb: 3, 
              mt: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              minHeight: 60
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Added Devices ({devices.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {devices.map((device, index) => (
                <Chip
                  key={index}
                  label={getDeviceLabel(device)}
                  onDelete={() => handleRemoveDevice(index)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                  disabled={isLoading}
                />
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Bulk Add MAC Addresses (Comma-Separated)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Paste a list of MAC addresses separated by commas. All addresses will use the same PI and device type settings below.
          </Typography>
          
          <TextField
            label="Paste MAC Addresses (comma-separated)"
            fullWidth
            multiline
            rows={4}
            value={bulkMacInput}
            onChange={(e) => setBulkMacInput(e.target.value)}
            placeholder="Example: ABC123456789, DEF987654321, GHI111222333"
            disabled={isLoading}
            helperText="Special characters inside each MAC are removed automatically when you add them."
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>
              PI (applies to all pasted MACs)
              {enableUserAssignment ? ' *' : ' (Optional)'}
            </InputLabel>
            <Select
              value={currentDevice.pi}
              label="PI (applies to all pasted MACs)"
              onChange={(e) => handleCurrentDeviceChange('pi', e.target.value)}
              disabled={isLoading}
              required={enableUserAssignment}
            >
              <MenuItem value="">{enableUserAssignment ? 'Select PI' : 'None (Optional)'}</MenuItem>
              <MenuItem value="pi-zero">PI - Zero</MenuItem>
              <MenuItem value="pi-4">PI - 4b</MenuItem>
              <MenuItem value="cm5">CM5</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            {!enableUserAssignment && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                PI is optional. Will be used for new devices if provided.
              </Typography>
            )}
          </FormControl>

          {currentDevice.pi === 'other' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Device Type (applies to all pasted MACs)</InputLabel>
              <Select
                value={currentDevice.deviceType}
                label="Device Type (applies to all pasted MACs)"
                onChange={(e) => handleCurrentDeviceChange('deviceType', e.target.value)}
                disabled={isLoading}
                required
              >
                {DEVICE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {enableUserAssignment && (
            <Box sx={{ mb: 2 }}>
              <UserAutocomplete
                value={selectedUser}
                onChange={setSelectedUser}
                disabled={isLoading}
                label="Assign to User (applies to all devices)"
                placeholder="Search user..."
              />
            </Box>
          )}

          {enableRegionSelection && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>
                Region (applies to all pasted MACs)
                {enableUserAssignment && !selectedUser && ' *'}
              </InputLabel>
              <Select
                value={currentDevice.region_code}
                label="Region (applies to all pasted MACs)"
                onChange={(e) => handleCurrentDeviceChange('region_code', e.target.value)}
                disabled={isLoading}
                required={enableUserAssignment && !selectedUser}
              >
                {REGIONS.map((r) => (
                  <MenuItem key={r.code} value={r.code}>
                    {r.code} - {r.name}
                  </MenuItem>
                ))}
              </Select>
              {enableUserAssignment && !selectedUser && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Region is required when user is not selected
                </Typography>
              )}
            </FormControl>
          )}

          <Button
            variant="outlined"
            startIcon={isCheckingDevices ? <CircularProgress size={16} /> : <AddIcon />}
            onClick={handleBulkAddMacAddresses}
            disabled={
              isLoading || 
              isCheckingDevices ||
              !bulkMacInput.trim() || 
              (enableUserAssignment && !currentDevice.pi) || 
              (enableUserAssignment && currentDevice.pi === 'other' && !currentDevice.deviceType) ||
              (enableRegionSelection && enableUserAssignment && !selectedUser && !currentDevice.region_code) ||
              (enableRegionSelection && !enableUserAssignment && !currentDevice.region_code)
            }
            fullWidth
          >
            {isCheckingDevices 
              ? 'Checking devices...' 
              : `Add All MAC Addresses (${bulkMacInput.split(',').filter(m => m.trim().length > 0).length} found)`
            }
          </Button>
        </Box>

        <TextField
          label="Notes (Optional - applies to all devices)"
          fullWidth
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes that apply to all devices..."
          disabled={isLoading}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || devices.length === 0}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          Submit {devices.length} Device{devices.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMultipleDevicesDialog;